/**
 * MONSTER TOWN — THE DOORSTEP, THE GUEST, AND THE PRESENT. VISIT CREW.
 *
 * Everything a kid can see and touch in the sibling feature. Four surfaces:
 *
 *   THE GATE      one button on the right rail, wearing the OTHER kid's monster
 *                 colour with their face on the corner. It is only there when
 *                 there is a town to walk into.
 *   THE DOORSTEP  a full-screen picture panel: their monster, a little map of
 *                 their actual town, what you have already left on their mat,
 *                 your album of pictures from over there — and two big buttons,
 *                 GIVE A PRESENT and GO IN.
 *   THE GUEST     inside their town: their face in the corner so it is never
 *                 unclear whose world this is, and exactly two controls — take a
 *                 picture, and GO HOME. Go home is 124 px, the biggest control
 *                 in the game, because "impossible to get stuck in" is a
 *                 requirement and not a hope.
 *   THE PRESENT   a wrapped box bobbing on your own rail when something is
 *                 waiting for you. Tap it, it opens, the thing is yours.
 *
 * LAW #4 IS ABSOLUTE HERE: there is not one word anywhere in this file's output.
 * Every `label` is an aria-label — read by a screen reader and by the QA tools,
 * never rendered. The only glyphs a kid sees are digits on a count badge, which
 * sit on top of a picture that already says the same thing.
 *
 * WHY THE GIFT SHELF IS ON THE DOORSTEP AND NOT INSIDE
 * `gifts.leave()` writes MY OWN outbox, and while a visit is open my own state
 * is not loaded — their town is. So the present is chosen on the mat, before you
 * go in, exactly the way the brief describes it ("leave a gift on their
 * doorstep"). This is not a limitation worked around; it is the shape the
 * read-only guarantee gives the feature, and it happens to be the nicer one.
 */

import './visit.css'
import { bus } from '../core/events'
import { visitAlbum } from '../core/daySave'
import { gifts, type GiftEntry } from '../core/gifts'
import { inventory } from '../core/inventory'
import { css, type PaletteName } from '../core/palette'
import { save } from '../core/save'
import { clock } from '../core/time'
import type { MonsterConfig, Phase, SaveSlot } from '../core/types'
import { app, type GameContext } from '../app/context'
import { button, el, onTap } from '../ui/dom'
import { iconEl } from '../ui/icons'
import { monsterPortrait } from '../ui/monsterArt'
import { panelShell } from '../ui/panels'
import { previewSlot } from '../ui/slots'
import { visitIconEl } from './icons'
import { PICKABLE, presentArt, type PresentDef } from './presents'
import { visit } from './state'

/** How many things from your own bag the shelf offers. A shelf, not a folder. */
const SHELF_BAG_MAX = 8
/**
 * THE ONLY SCREENS THE GATE BELONGS ON. Anything else — the monster creator, the
 * shop, photo mode, the slot picker, boot — is a full-screen takeover somebody
 * else owns, and a gate floating on top of it is at best clutter and at worst a
 * button a kid taps expecting the sheet they are looking at.
 */
const WORLD_PHASES: readonly Phase[] = ['town', 'houseInterior', 'venueInterior']
/** Postcard border as a fraction of the shorter side. Matches photo mode. */
const FRAME = 0.045

type Page = 'main' | 'shelf'

/**
 * A LITTLE MAP OF A TOWN, from that slot's own save data. Same idea as the
 * slot picker's, drawn here because this crew does not edit the HUD crew's
 * files. Derived every time, so it can never go stale and it costs a save
 * nothing.
 */
const MAP_COLORS: readonly (readonly [string, PaletteName])[] = [
  ['path', 'path'],
  ['road', 'road'],
  ['water', 'water'],
  ['pond', 'water'],
  ['tree', 'leaf'],
  ['plant', 'leafLight'],
  ['flower', 'flowerPink'],
  ['fence', 'woodLight'],
  ['roof', 'roofRed'],
  ['wall', 'wallCream'],
  ['floor', 'floorWood'],
  ['house', 'roofRed'],
  ['shop', 'roofTeal'],
  ['venue', 'roofTeal'],
  ['town', 'metal'],
]

function dotColor(kind: string): string {
  for (const [frag, name] of MAP_COLORS) {
    if (kind.includes(frag)) return css(name)
  }
  return css('woodLight')
}

/**
 * WHICH PIECE WINS WHEN TWO LAND IN THE SAME MAP CELL. Bigger number wins.
 * A house that a kid can see from across their town should not be hidden by the
 * path running past its door, so the map is not "last one drawn".
 */
const MAP_RANK: readonly (readonly [string, number])[] = [
  ['house', 6],
  ['shop', 6],
  ['venue', 6],
  ['roof', 6],
  ['wall', 5],
  ['water', 5],
  ['pond', 5],
  ['tree', 4],
  ['plant', 3],
  ['flower', 3],
  ['fence', 2],
  ['road', 1],
  ['path', 1],
  ['floor', 1],
]

function rankOf(kind: string): number {
  for (const [frag, r] of MAP_RANK) {
    if (kind.includes(frag)) return r
  }
  return 3
}

/**
 * WHAT SHAPE A PIECE IS ON THE LITTLE MAP. Four, and no more: the map is 176 px
 * across and a fifth shape would be a shape nobody can tell from another one.
 * Matched on the same substrings as `MAP_COLORS`, so a new def gets a sensible
 * shape for free and a new SHAPE is a line in this table — never engine work.
 */
type MapShape = 'flat' | 'low' | 'tree' | 'building'
const MAP_SHAPE: readonly (readonly [string, MapShape])[] = [
  ['road', 'flat'],
  ['path', 'flat'],
  ['floor', 'flat'],
  ['water', 'flat'],
  ['pond', 'flat'],
  ['tree', 'tree'],
  ['plant', 'tree'],
  ['house', 'building'],
  ['shop', 'building'],
  ['venue', 'building'],
  ['roof', 'building'],
  ['wall', 'building'],
]

function shapeOf(kind: string): MapShape {
  for (const [frag, s] of MAP_SHAPE) {
    if (kind.includes(frag)) return s
  }
  return 'low'
}

/* ------------------------------------------------------------------------- *
 * THE LITTLE MAP IS DRAWN THE WAY THE GAME IS DRAWN: 2:1 ISOMETRIC.
 * One shared pen, so nothing here can drift into a second art language.
 * ------------------------------------------------------------------------- */

/** The diamond top face of one cell, centred on (sx, sy). */
function isoFace(c: CanvasRenderingContext2D, sx: number, sy: number, tw: number, th: number): void {
  c.beginPath()
  c.moveTo(sx, sy - th / 2)
  c.lineTo(sx + tw / 2, sy)
  c.lineTo(sx, sy + th / 2)
  c.lineTo(sx - tw / 2, sy)
  c.closePath()
}

/** Fill the current path in `colour`, then darken it toward ink by `k`. */
function fillPath(c: CanvasRenderingContext2D, colour: string, k = 0): void {
  c.fillStyle = colour
  c.fill()
  if (k > 0) {
    c.globalAlpha = k
    c.fillStyle = css('ink')
    c.fill()
    c.globalAlpha = 1
  }
}

/** The soft ink line every piece of art in this game wears. */
function edge(c: CanvasRenderingContext2D, tw: number, alpha = 0.4): void {
  c.lineWidth = Math.max(1, tw * 0.07)
  c.strokeStyle = css('ink')
  c.globalAlpha = alpha
  c.stroke()
  c.globalAlpha = 1
}

/** A flat tile lying on the ground — a road, a path, a pond. */
function isoFlat(c: CanvasRenderingContext2D, sx: number, sy: number, tw: number, th: number, colour: string): void {
  isoFace(c, sx, sy, tw, th)
  fillPath(c, colour)
  edge(c, tw, 0.22)
}

/** A chunky box standing on the cell: top face plus its two lit-and-shaded sides. */
function isoBlock(
  c: CanvasRenderingContext2D,
  sx: number,
  sy: number,
  tw: number,
  th: number,
  h: number,
  top: string,
  side: string,
): void {
  // left face (away from the light)
  c.beginPath()
  c.moveTo(sx - tw / 2, sy - h)
  c.lineTo(sx, sy - h + th / 2)
  c.lineTo(sx, sy + th / 2)
  c.lineTo(sx - tw / 2, sy)
  c.closePath()
  fillPath(c, side, 0.3)
  edge(c, tw, 0.32)
  // right face
  c.beginPath()
  c.moveTo(sx + tw / 2, sy - h)
  c.lineTo(sx, sy - h + th / 2)
  c.lineTo(sx, sy + th / 2)
  c.lineTo(sx + tw / 2, sy)
  c.closePath()
  fillPath(c, side, 0.14)
  edge(c, tw, 0.32)
  // the top, last, so its outline is unbroken
  isoFace(c, sx, sy - h, tw, th)
  fillPath(c, top)
  edge(c, tw, 0.42)
}

/** A tree: a trunk with a leafy ball on it, because a green box is a bush. */
function isoTree(c: CanvasRenderingContext2D, sx: number, sy: number, tw: number, th: number, leafy: string): void {
  const trunkH = tw * 0.34
  const tWide = Math.max(2, tw * 0.2)
  isoBlock(c, sx, sy, tWide, tWide / 2, trunkH, css('trunk'), css('trunkDark'))
  const r = tw * 0.4
  c.beginPath()
  c.arc(sx, sy - trunkH - r * 0.55, r, 0, Math.PI * 2)
  fillPath(c, leafy)
  edge(c, tw, 0.42)
  void th
}

/**
 * A LITTLE MAP OF A TOWN — and it has to READ as a place.
 *
 * The first pass was a plain green rounded square carrying about fourteen 20 px
 * coloured pixels, and the critic was right that to a pre-reader that is a blank
 * green card. It is also the entire visual promise of what is behind the door,
 * so it is the last thing in this feature that should look like debug output.
 * Three things were wrong and all three are fixed here:
 *
 *  - IT WAS DRAWN ON BARE GRASS. There is now a plot: a warm mown board with a
 *    soft edge, so the town sits ON something instead of floating in green.
 *  - THE PIECES WERE FLAT SQUARES with no outline, in a world whose whole art
 *    language is chunky silhouettes with a soft ink line. They are rounded,
 *    outlined tiles now, with a shadow, and they never draw below 7 px.
 *  - IT ZOOMED OUT ON A SMALL TOWN. `span` was floored at 6 but never capped, so
 *    a kid whose pieces were spread across the plot got dust. The fit now grows
 *    the blocks until they read, and only then starts fitting more in.
 *
 * ROUND 2 — AND IT WAS STILL DUST, because growing the blocks is not enough when
 * the thing being drawn is ONE TILE PER PLACED PIECE AT TRUE GRID SCALE. A town
 * twenty grid squares across, in a 160 px box, is 7 px specks with gaps between
 * them however chunky each speck is, and measured on the real demo town that is
 * exactly what came out: fourteen 7 px squares scattered over a green card.
 *
 * A MAP IS NOT A SCATTER PLOT. It is a small picture of a place. The town is now
 * FOLDED into at most `MAP_GRID` cells a side; the most building-ish piece in
 * each cell is what gets drawn (see `MAP_RANK` — a house is not hidden by the
 * path running past its door); and those cells are drawn big enough to read.
 * The shape of the town survives, the specks do not, and a town with two pieces
 * in it draws two big blocks rather than two dots.
 *
 * ROUND 3 — AND IT STILL READ AS CONFETTI, because folding and growing were
 * still answering the wrong question. Measured on the recapture: fourteen
 * chunky, outlined, correctly-coloured squares — LYING FLAT, AXIS-ALIGNED, ON A
 * PLAIN GREEN RECTANGLE. Every one of the three earlier fixes had landed and the
 * critic's verdict did not move a millimetre: *"a plain green rectangle with
 * about a dozen small coloured squares dotted on it — it bears no resemblance to
 * the isometric town it represents."*
 *
 * That is the whole diagnosis. **THE GAME IS ISOMETRIC AND THE MAP WAS A PLAN
 * VIEW.** A three-year-old has never seen their town from directly overhead;
 * they have only ever seen it in 2:1 iso with chunky boxes and soft ink lines,
 * which is the one picture in this build they can already read. A top-down
 * square is not a small version of that — it is a different drawing of the same
 * data, and to a pre-reader a different drawing is a different thing. It is the
 * same mistake the sleepy bubble's bed made (a mattress modelled in plan and
 * then billboarded, so the camera only ever saw its edge — "a brown box with a
 * teal stripe"), and it has the same fix: draw it the way it is SEEN.
 *
 * So the map is now the town, small:
 *   - a 2:1 ISOMETRIC ground island, mown in two greens, sitting on a soft sky;
 *   - one CHUNKY BOX per cell, with a lit face, a shaded face and the game's own
 *     ink line — a house is a house-shaped thing, not a red pixel;
 *   - four shapes and no more (`MAP_SHAPE`): flat for roads, paths and water;
 *     a low block for fences and flowers; a leafy ball on a trunk for a tree;
 *     a tall block with a coloured roof for anything you can walk into;
 *   - drawn back to front, so the near row overlaps the far one and the picture
 *     has depth instead of being a grid of stamps.
 *
 * A town with nothing placed in it yet is not an error and must not look like
 * one: it draws the empty island with a path across it, which is what it is.
 */
/** The most cells a map is ever divided into, per side. */
const MAP_GRID = 9
/** Nothing on a map is ever drawn narrower than this. It has to read. */
const MAP_MIN_TILE = 13
/** Nor wider than this: a two-piece town must not be two boulders. */
const MAP_MAX_TILE = 40
/**
 * THE MAP IS A LANDSCAPE PICTURE, NOT A SQUARE ONE.
 *
 * An iso island is (cellsX+cellsZ) half-tiles wide and half that tall, so in a
 * SQUARE box the widest fit that still gets both axes in leaves the bottom and
 * top thirds as empty sky — measured on the first r3 capture: an island 89 px
 * tall inside a 176 px box, floating in blue. Fitting to the shape the drawing
 * actually is gets a bigger town for the same card, and a landscape frame reads
 * as a little photograph of their place, which is what it is.
 */
const MAP_W = 200
const MAP_H = 134

function townMap(
  dots: readonly { x: number; z: number; kind: string }[],
  boxW: number,
  boxH: number,
): HTMLCanvasElement {
  const dpr = Math.min(2, window.devicePixelRatio || 1)
  const cv = document.createElement('canvas')
  cv.width = Math.round(boxW * dpr)
  cv.height = Math.round(boxH * dpr)
  cv.style.width = `${boxW}px`
  cv.style.height = `${boxH}px`
  cv.style.display = 'block'
  const c = cv.getContext('2d')
  if (c === null) return cv
  c.scale(dpr, dpr)
  c.lineJoin = 'round'

  // -- the sky the island sits in ------------------------------------------
  const sky = c.createLinearGradient(0, 0, 0, boxH)
  sky.addColorStop(0, css('skyTop'))
  sky.addColorStop(1, css('skyHorizon'))
  c.fillStyle = sky
  c.fillRect(0, 0, boxW, boxH)

  // -- fold the town into cells. A one-piece town gets one cell, not a speck --
  let minX = Infinity
  let maxX = -Infinity
  let minZ = Infinity
  let maxZ = -Infinity
  for (const d of dots) {
    if (d.x < minX) minX = d.x
    if (d.x > maxX) maxX = d.x
    if (d.z < minZ) minZ = d.z
    if (d.z > maxZ) maxZ = d.z
  }
  const empty = dots.length === 0
  const wide = empty ? 5 : maxX - minX + 1
  const tall = empty ? 5 : maxZ - minZ + 1
  const cellsX = Math.max(1, Math.min(MAP_GRID, wide))
  const cellsZ = Math.max(1, Math.min(MAP_GRID, tall))
  const cells = new Map<string, { cx: number; cz: number; kind: string; rank: number }>()
  for (const d of dots) {
    const cx = Math.min(cellsX - 1, Math.floor(((d.x - minX) / wide) * cellsX))
    const cz = Math.min(cellsZ - 1, Math.floor(((d.z - minZ) / tall) * cellsZ))
    const key = `${cx},${cz}`
    const rank = rankOf(d.kind)
    const had = cells.get(key)
    if (had === undefined || rank > had.rank) cells.set(key, { cx, cz, kind: d.kind, rank })
  }

  // -- fit the island to the card, in BOTH axes -----------------------------
  // An iso footprint is (cellsX+cellsZ) half-tiles wide and half that tall, plus
  // the height of the tallest thing standing on it. Fit whichever binds first.
  const padX = boxW * 0.05
  const padY = boxH * 0.07
  const roomX = boxW - padX * 2
  const roomY = boxH - padY * 2
  const spanCells = cellsX + cellsZ
  const TALL = 0.78 // the tallest block, as a fraction of one tile's width
  const tw = Math.max(
    MAP_MIN_TILE,
    Math.min(MAP_MAX_TILE, (2 * roomX) / spanCells, roomY / (spanCells / 4 + TALL)),
  )
  const th = tw / 2
  const w = (spanCells * tw) / 2
  const h = (spanCells * th) / 2 + tw * TALL
  const ox = (boxW - w) / 2 + (cellsZ * tw) / 2
  const oy = (boxH - h) / 2 + th / 2 + tw * TALL
  const sxOf = (cx: number, cz: number): number => ox + ((cx - cz) * tw) / 2
  const syOf = (cx: number, cz: number): number => oy + ((cx + cz) * th) / 2

  // -- the ground island: every cell, mown in two greens ---------------------
  // Drawn as one silhouette first so the island has a single soft edge rather
  // than a seam per tile, then the mowing on top of it.
  c.beginPath()
  for (let cz = 0; cz < cellsZ; cz++) {
    for (let cx = 0; cx < cellsX; cx++) {
      const sx = sxOf(cx, cz)
      const sy = syOf(cx, cz)
      c.moveTo(sx, sy - th / 2)
      c.lineTo(sx + tw / 2, sy)
      c.lineTo(sx, sy + th / 2)
      c.lineTo(sx - tw / 2, sy)
      c.closePath()
    }
  }
  c.fillStyle = css('grass')
  c.fill()
  c.lineWidth = Math.max(2, tw * 0.16)
  c.strokeStyle = css('ink')
  c.globalAlpha = 0.16
  c.stroke()
  c.globalAlpha = 1
  for (let cz = 0; cz < cellsZ; cz++) {
    for (let cx = 0; cx < cellsX; cx++) {
      if ((cx + cz) % 2 !== 0) continue
      isoFace(c, sxOf(cx, cz), syOf(cx, cz), tw, th)
      c.globalAlpha = 0.5
      c.fillStyle = css('grassLight')
      c.fill()
      c.globalAlpha = 1
    }
  }

  if (empty) {
    // An empty island is an invitation, not a blank: a path already runs across
    // it, the way the slot picker's sprout says "a town goes here".
    for (let i = 0; i < cellsX; i++) isoFlat(c, sxOf(i, cellsZ - 1 - i), syOf(i, cellsZ - 1 - i), tw, th, css('path'))
    return cv
  }

  // -- what they built, BACK TO FRONT so the picture has depth ---------------
  const order = [...cells.values()].sort((a, b) => a.cx + a.cz - (b.cx + b.cz))
  for (const cell of order) {
    const sx = sxOf(cell.cx, cell.cz)
    const sy = syOf(cell.cx, cell.cz)
    const colour = dotColor(cell.kind)
    switch (shapeOf(cell.kind)) {
      case 'flat':
        isoFlat(c, sx, sy, tw, th, colour)
        break
      case 'low':
        isoBlock(c, sx, sy, tw * 0.82, th * 0.82, tw * 0.22, colour, colour)
        break
      case 'tree':
        isoTree(c, sx, sy, tw, th, colour)
        break
      default:
        // A ROOF ON WALLS. The colour table already knows a house is red and a
        // venue is teal; that colour belongs on the roof, where a kid looks.
        isoBlock(c, sx, sy, tw * 0.92, th * 0.92, tw * TALL, colour, css('wallCream'))
        break
    }
  }
  return cv
}

/** One present picture, from either icon set. */
function presentPic(itemId: string, sizePx: number): HTMLElement {
  const art = presentArt(itemId)
  return art.iconSet === 'visit'
    ? visitIconEl(art.icon, sizePx, { f: art.tint })
    : iconEl(art.icon, sizePx, { f: art.tint })
}

function defPic(def: PresentDef, sizePx: number): HTMLElement {
  return def.iconSet === 'visit'
    ? visitIconEl(def.icon, sizePx, def.tint === undefined ? {} : { f: def.tint })
    : iconEl(def.icon, sizePx, def.tint === undefined ? {} : { f: def.tint })
}

export interface VisitTestApi {
  readonly ready: boolean
  canVisit(): boolean
  visiting(): boolean
  otherSlot(): SaveSlot
  page(): Page | null
  openDoorstep(): boolean
  openGiftShelf(): boolean
  closeDoorstep(): void
  enter(): Promise<boolean>
  leave(): Promise<boolean>
  leaveGift(itemId: string): boolean
  waiting(): number
  openPresent(): boolean
  takePhoto(): boolean
  album(): { id: string; slot: string; hasImage: boolean }[]
  /**
   * Every control THIS crew built, measured from its own rect. `shown` is false
   * for one that is in the DOM but deliberately off the screen right now — the
   * gate while this crew's own sheet is up, for instance. A tool asking "is
   * everything a kid can see big enough" must filter on it; one asking "is
   * anything the wrong size" should not.
   */
  buttons(): { label: string; w: number; h: number; shown: boolean }[]
  /** Client-space box of one of this crew's controls, for a real finger tap. */
  rect(label: string): { x: number; y: number; w: number; h: number } | null
  /** Which town-changing HUD controls are currently off screen. */
  hidden(): string[]
}

declare global {
  interface Window {
    __visit?: VisitTestApi
  }
}

export function installVisitUi(ctx: GameContext): void {
  const hud = ctx.hud
  const layer = el('div', 'mt-visit-layer')
  hud.append(layer)

  // -- the fade between two towns ------------------------------------------
  const fade = el('div', 'mt-visit-fade')
  layer.append(fade)
  visit.setFader(
    (dir) =>
      new Promise<void>((resolve) => {
        fade.dataset['on'] = dir === 'out' ? 'true' : 'false'
        window.setTimeout(resolve, 280)
      }),
  )

  // -- the right-hand rail: the gate, and anything waiting for you ----------
  const rail = el('div', 'mt-visit-rail')
  layer.append(rail)

  const gate = button(
    { icon: undefined, label: 'Go and see the other town', className: 'big mt-visit-gate', cue: 'door_knock' },
    () => {
      openPanel('main')
    },
  )
  const gateFace = el('div', 'mt-visit-face')
  gate.append(visitIconEl('gate', 56), gateFace)

  const waiting = button(
    { label: 'A present is waiting for you', className: 'big mt-visit-waiting', cue: 'mail_open' },
    () => {
      openPresent()
    },
  )
  const waitingCount = el('span', 'mt-visit-count mt-num')
  waiting.append(visitIconEl('present', 58), waitingCount)

  // -- the guest chrome -----------------------------------------------------
  const guest = el('div', 'mt-visit-guest')
  const guestFace = el('div', 'mt-visit-portrait')
  guest.append(guestFace)

  const bar = el('div', 'mt-visit-bar')
  const photoBtn = button(
    { label: 'Take a picture of their town', className: 'big', cue: null },
    () => {
      takeSouvenir()
    },
  )
  photoBtn.append(iconEl('camera', 54))
  const homeBtn = button(
    { label: 'Go back to my own town', className: 'big mt-visit-home', cue: null },
    () => {
      void visit.leave()
    },
  )
  homeBtn.append(visitIconEl('goHome', 72))
  bar.append(photoBtn, homeBtn)

  // -- panel + overlays -----------------------------------------------------
  let panelNode: HTMLElement | null = null
  let page: Page | null = null
  let overlay: HTMLElement | null = null

  const closeOverlay = (): void => {
    overlay?.remove()
    overlay = null
  }

  const closePanel = (): void => {
    panelNode?.remove()
    panelNode = null
    page = null
    hud.dataset['panel'] = '0'
    layer.dataset['sheet'] = '0'
    closeOverlay()
    bus.emit('audio:cue', { cue: 'ui_close' })
  }

  /**
   * Opening this crew's sheet closes whatever the HUD had open, through the
   * HUD's own published contract (`ui:panel` with a null panel). Two full-screen
   * sheets can never stack, and no crew reaches into another crew's host.
   */
  function openPanel(which: Page): boolean {
    if (!save.hasSave(visit.other) || save.visiting) return false
    if (window.__ui?.panel() != null) bus.emit('ui:panel', { panel: null })
    panelNode?.remove()
    const built = which === 'shelf' ? buildShelf() : buildDoorstep()
    panelNode = built
    page = which
    layer.append(built)
    /**
     * THE DOORSTEP IS A FULL-SCREEN SHEET, and a sheet is a card floating on the
     * town now rather than an opaque cream fill (`.mt-panel`, ui.css). The coin
     * purse and the hammer have to leave the screen the same way they do under
     * a HUD panel. This crew's sheet does not go through `PanelHost`, so it
     * raises the HUD's own flag itself. Same reach, same justification and same
     * one-way-ness as the five display rules at the bottom of `visit.css`: a
     * data attribute the HUD already publishes, carrying no behaviour.
     *
     * AND THIS CREW'S OWN RAIL GOES WITH THEM. The gate wears the host's face,
     * so with the doorstep open it was a second, smaller copy of the portrait on
     * the card — bobbing, in the corner, on the one screen whose whole job is to
     * say "this is THAT monster". `data-sheet` is this layer's own attribute on
     * this layer's own node; the rule is four lines of visit.css.
     */
    hud.dataset['panel'] = '1'
    layer.dataset['sheet'] = '1'
    bus.emit('audio:cue', { cue: 'ui_open' })
    return true
  }

  // -- the doorstep ---------------------------------------------------------

  function buildDoorstep(): HTMLElement {
    const slot = visit.other
    const p = previewSlot(slot)
    const parts = panelShell({ icon: 'who', onBack: closePanel })
    parts.root.classList.add('mt-visit-panel')

    /**
     * WHOSE DOOR IS THIS? ONE FACE, AND IT IS THE BIG ONE.
     *
     * The shell's generic 64 px "two little faces" glyph said *somebody*, which
     * is the one thing this screen must not say, so round 2 put the host's own
     * monster in the title. That was right about the glyph and wrong about the
     * count: the capture then had the same monster's face on the glass THREE
     * times at once — 44 px on the gate still bobbing behind the sheet, 76 px in
     * the header, 176 px on the card — and to a three-year-old who cannot read
     * either name, one monster wearing three sizes in three places is not a
     * consistent design, it is three monsters.
     *
     * A doorstep asks exactly one question and it is already answered, in the
     * biggest picture on the screen, in that monster's own colour. So the header
     * is now just the way back: the title is REMOVED rather than re-dressed, the
     * gate goes off the rail while the sheet is up (see `openPanel`), and the
     * card's 176 px portrait is the only face left.
     */
    parts.head.querySelector('.mt-panel-title')?.remove()

    const wrap = el('div', 'mt-visit-doorstep')

    // THEIR MONSTER AND THEIR TOWN, side by side. A pre-reader knows whose
    // house this is from the colour of the card before they look at anything.
    const card = el('div', 'mt-visit-card')
    card.style.setProperty('--doorstep-tint', css(p.look.color))
    const portrait = el('div', 'mt-visit-portrait')
    portrait.append(monsterPortrait(p.look, 168))
    const map = el('div', 'mt-visit-map')
    map.append(townMap(p.dots, MAP_W, MAP_H))
    card.append(portrait, map)
    wrap.append(card)

    // WHAT IS ALREADY ON THEIR MAT. Everything you have ever left them; it stays
    // in your outbox for ever, so this is a little record of being kind.
    const mat = el('div', 'mt-visit-mat')
    /**
     * WOOL AND JUTE, FROM THE PALETTE — never a `ui*` token and never a hex.
     * The interiors crew reached the same conclusion about the doormat inside
     * the house ("the mat is a doormat, not a button"): a mat drawn in the two
     * cream UI tokens is a pale rectangle in a pale card, which is a HOLE in the
     * card, not a thing lying on a step. Handed to CSS from here because the
     * palette lives in TS and this file is where a colour decision belongs.
     */
    mat.style.setProperty('--mat-wool', css('woodLight'))
    mat.style.setProperty('--mat-thread', css('wood'))
    const sent = gifts.sent().filter((g) => g.toSlot === slot)
    mat.dataset['empty'] = String(sent.length === 0)
    if (sent.length === 0) {
      /**
       * NOTHING GOES ON AN EMPTY MAT, BECAUSE THE ROW *IS* THE MAT NOW.
       *
       * Round 2 put a 72 px `doormat` picture in the middle of the row, and the
       * critic read the result exactly right: *"a wide cream pill with one small
       * doormat icon lost in the middle, which reads as broken rather than as
       * 'nothing here yet'."* A picture of a mat, floating in the middle of a
       * bar that is not a mat, is two objects saying one thing badly.
       *
       * The row itself is woven now, with a fringe at each end and a stitched
       * border (`.mt-visit-mat`, visit.css) — so it is a doormat lying on their
       * step at the size a doormat actually is, and "empty" is a mat with
       * nothing on it, which is what a doorstep with nothing on it looks like.
       * Nothing is dimmed, nothing is dashed, nothing is disabled.
       */
    } else {
      // LAW #1 IN SPIRIT: a three-year-old is going to poke the flower they
      // left, and a picture that does nothing reads as broken. Each one wobbles
      // and chimes. It cannot be taken back — nothing here is ever undone.
      for (const g of sent.slice(-6)) {
        const item = el('div', 'mt-visit-matitem')
        item.setAttribute('role', 'img')
        item.setAttribute('aria-label', 'Something you left for them')
        item.append(presentPic(g.itemId, 56))
        onTap(item, () => {
          item.animate(
            [{ transform: 'rotate(0)' }, { transform: 'rotate(-9deg)' }, { transform: 'rotate(7deg)' }, { transform: 'rotate(0)' }],
            { duration: 380 },
          )
        }, 'sparkle_soft')
        mat.append(item)
      }
    }
    wrap.append(mat)

    // THE ALBUM: pictures you brought back from over there.
    const album = el('div', 'mt-visit-album')
    const shots = visit.souvenirs().filter((s) => s.slot === slot)
    album.dataset['on'] = String(shots.length > 0)
    for (const s of shots.slice(-4)) {
      const frame = el('div', 'mt-visit-shot')
      frame.setAttribute('role', 'button')
      frame.setAttribute('aria-label', 'Look at this picture')
      if (s.url === null) {
        // From an earlier session: the id survived, the pixels did not — the
        // `visit` save section holds photo IDS, never image bytes. Draw the town
        // it was a picture OF, rather than a broken frame.
        frame.append(townMap(p.dots, 176, 128))
        onTap(frame, () => {
          frame.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.06)' }, { transform: 'scale(1)' }], {
            duration: 320,
          })
        }, 'sparkle_soft')
      } else {
        const img = new Image()
        img.src = s.url
        img.alt = ''
        frame.append(img)
        onTap(frame, () => showPostcard(s.url ?? ''), 'shutter')
      }
      album.append(frame)
    }
    wrap.append(album)
    parts.body.append(wrap)

    const actions = el('div', 'mt-visit-actions')
    const giftBtn = button(
      { label: 'Leave a present on their doorstep', className: 'big', cue: null },
      () => {
        openPanel('shelf')
      },
    )
    giftBtn.append(visitIconEl('present', 58))
    const goBtn = button(
      { label: 'Go into their town', className: 'big primary', cue: null },
      () => {
        closePanel()
        void visit.enter()
      },
    )
    goBtn.append(visitIconEl('gate', 60))
    actions.append(giftBtn, goBtn)
    parts.foot.append(actions)
    return parts.root
  }

  // -- the gift shelf -------------------------------------------------------

  function buildShelf(): HTMLElement {
    const parts = panelShell({ icon: 'bag', onBack: () => openPanel('main') })
    parts.root.classList.add('mt-visit-panel')
    const shelf = el('div', 'mt-visit-shelf')

    const add = (itemId: string, pic: HTMLElement, count: number, label: string): void => {
      const tile = el('div', 'mt-present')
      tile.setAttribute('role', 'button')
      tile.setAttribute('aria-label', label)
      tile.append(pic)
      if (count > 1) {
        const n = el('span', 'mt-num')
        n.textContent = String(count)
        tile.append(n)
      }
      onTap(tile, () => {
        leaveGift(itemId)
      })
      shelf.append(tile)
    }

    // 1. things you can just pick. Always there, so a kid can always give.
    for (const def of PICKABLE) add(def.id, defPic(def, 74), 1, `Leave ${def.devName}`)

    // 2. anything in your own bag — cooked, caught, grown or bought. Arrives
    //    free as the other crews land; this file supplies the picture only.
    const bag = Object.entries(inventory.all())
      .filter(([id, n]) => id !== '' && n > 0)
      .slice(0, SHELF_BAG_MAX)
    for (const [id, n] of bag) add(id, presentPic(id, 74), n, `Leave one from your bag`)

    parts.body.append(shelf)
    return parts.root
  }

  /**
   * LEAVE ONE. Writes MY OWN outbox and nothing else — see `src/core/gifts.ts`.
   * Their town is not opened, not read for writing, and not touched.
   */
  function leaveGift(itemId: string): boolean {
    if (save.visiting) return false
    const slot = visit.other
    // Something out of the bag really leaves the bag. A pickable costs nothing.
    const fromBag = inventory.count(itemId) > 0
    if (fromBag && !inventory.take(itemId, 1, 'gift')) return false
    const entry = gifts.leave(slot, itemId, 1, clock.now().day)
    if (entry === null) {
      if (fromBag) inventory.add(itemId, 1, 'gift:undo')
      return false
    }
    bus.emit('audio:cue', { cue: 'chime_happy' })
    bus.emit('ui:toast', { icon: 'heart', ms: 1400 })
    openPanel('main')
    return true
  }

  // -- a present waiting for you -------------------------------------------

  function openPresent(): boolean {
    const next = gifts.waiting()[0]
    if (next === undefined) return false
    showPresentOpening(next)
    return true
  }

  function showPresentOpening(entry: GiftEntry): void {
    closeOverlay()
    const from = previewSlot(entry.fromSlot)
    const card = el('div', 'mt-visit-postcard')

    const box = el('div', 'mt-visit-portrait')
    box.append(visitIconEl('present', 132))
    // WHO IT IS FROM, on top of the card. It used to be an absolutely-positioned
    // badge in the corner and it sat half on top of the open button — read in
    // `shots/visit/11-it-is-a-flower.png`.
    const who = el('div', 'mt-visit-from')
    who.style.setProperty('--from-tint', css(from.look.color))
    who.append(monsterPortrait(from.look, 76))

    const row = el('div', 'mt-visit-actions')
    // Green tile, white tick: the ONE confirm picture in the whole app. The
    // button shipped with no icon at all in the first pass — a blank green
    // square, caught by reading `shots/visit/10-opening-it.png`.
    const openBtn = button({ icon: 'tick', label: 'Open the present', className: 'big go', cue: 'gift_open' }, () => {
      // The moment it is opened it is really yours: it goes in the bag and it
      // comes off the doorstep. Both writes land in MY OWN save.
      gifts.open(entry.id)
      inventory.add(entry.itemId, entry.count, 'gift')
      box.replaceChildren(presentPic(entry.itemId, 132))
      row.replaceChildren(
        button({ icon: 'tick', label: 'Lovely', className: 'big go', cue: 'sparkle_soft' }, () => {
          closeOverlay()
          paint()
        }),
      )
      bus.emit('ui:toast', { icon: 'star', ms: 1400 })
      paint()
    })
    row.append(openBtn)

    card.append(who, box, row)
    layer.append(card)
    overlay = card
    bus.emit('audio:cue', { cue: 'npc_hello' })
  }

  // -- a picture of their town ---------------------------------------------

  /**
   * RENDER THEN READ, IN THE SAME TASK. The renderer has no
   * `preserveDrawingBuffer`, so `toDataURL()` on any later task comes back
   * blank. Do not put an `await` between these two statements.
   */
  function takeSouvenir(): boolean {
    if (!save.visiting) return false
    ctx.renderer.render(ctx.camera.camera, 16)
    let url = ''
    try {
      url = ctx.canvas.toDataURL('image/png')
    } catch {
      url = ''
    }
    bus.emit('audio:cue', { cue: 'shutter' })
    if (url === '' || url.length < 2000) return false

    const img = new Image()
    img.onload = (): void => {
      const w = img.naturalWidth
      const h = img.naturalHeight
      const pad = Math.round(Math.min(w, h) * FRAME)
      const out = document.createElement('canvas')
      out.width = w + pad * 2
      out.height = h + pad * 2
      const c = out.getContext('2d')
      if (c === null) return
      c.fillStyle = css('uiPanel')
      c.fillRect(0, 0, out.width, out.height)
      c.drawImage(img, pad, pad)
      c.strokeStyle = css('ink')
      c.lineWidth = Math.max(3, pad * 0.24)
      c.strokeRect(pad, pad, w, h)
      const cardUrl = out.toDataURL('image/png')
      const s = visit.keepSouvenir(cardUrl)
      if (s === null) return
      showPostcard(cardUrl)
      bus.emit('ui:toast', { icon: 'camera', ms: 1200 })
    }
    img.src = url
    return true
  }

  function showPostcard(url: string): void {
    closeOverlay()
    const card = el('div', 'mt-visit-postcard')
    const shown = new Image()
    shown.src = url
    shown.alt = ''
    card.append(shown)
    card.append(
      button({ icon: 'tick', label: 'Keep it', className: 'big go', cue: 'sparkle_soft' }, () => {
        closeOverlay()
      }),
    )
    layer.append(card)
    overlay = card
  }

  // -- painting -------------------------------------------------------------

  function paintGate(look: MonsterConfig): void {
    gate.style.setProperty('--gate-tint', css(look.color))
    gateFace.replaceChildren(monsterPortrait(look, 44))
  }

  function paint(): void {
    const visiting = save.visiting
    hud.dataset['visiting'] = visiting ? '1' : '0'

    // -- the rail (only when we are at home) --------------------------------
    rail.replaceChildren()
    if (!visiting) {
      const slot = visit.other
      if (save.hasSave(slot)) {
        paintGate(previewSlot(slot).look)
        rail.append(gate)
      }
      const n = gifts.waiting().length
      if (n > 0) {
        waitingCount.textContent = String(n)
        waitingCount.style.display = n > 1 ? 'flex' : 'none'
        rail.append(waiting)
      }
    }

    // -- the guest chrome (only when we are a guest) ------------------------
    if (visiting) {
      const host = save.visitSlot
      const look = host === null ? null : previewSlot(host).look
      if (look !== null) {
        guest.style.setProperty('--guest-tint', css(look.color))
        guestFace.replaceChildren(monsterPortrait(look, 96))
      }
      if (!layer.contains(guest)) layer.append(guest)
      if (!layer.contains(bar)) layer.append(bar)
    } else {
      guest.remove()
      bar.remove()
    }
  }

  // -- wiring ---------------------------------------------------------------

  // The save layer's own announcement that the read-only window opened/closed.
  // This crew turns it into the crew-facing pair the contract declares.
  bus.on('save:visitBegan', (e) => {
    closePanel()
    bus.emit('visit:entered', { slot: e.slot, host: e.host })
  })
  bus.on('save:visitEnded', (e) => {
    bus.emit('visit:left', { slot: e.slot, host: e.host })
  })
  bus.on('visit:entered', paint)
  bus.on('visit:left', paint)
  bus.on('gift:left', paint)
  bus.on('gift:received', paint)
  bus.on('save:slotChanged', paint)
  bus.on('save:loaded', () => {
    // Album ids come back from OUR OWN save section; the pixels do not (they are
    // never written — see `daySave.ts`, `photos` is ids only).
    visit.restoreAlbumIds(visitAlbum().photos)
    paint()
  })
  /**
   * THE GATE ONLY EXISTS ON THE WORLD SCREENS.
   *
   * Two gates on it, because two different things take the screen over:
   *  - a HUD PANEL (shop, photo, the slot picker) announces itself on `ui:panel`;
   *  - the MONSTER CREATOR does not — it is the monster crew's own sheet, driven
   *    off `app:phaseChanged` — so a gate button would have floated on top of a
   *    kid making their monster, which is also the first thing they ever see.
   * The first-run tutorial is the third: its whole job is to be the only thing
   * worth tapping, and a bobbing gate beside a pointing paw is exactly the
   * distraction it is designed not to have.
   */
  /**
   * ============ ONE WINDOW PER FIRST RUN. IT NEVER COMES BACK. ============
   *
   * THE HISTORY MATTERS, because this is the third shape this rule has had and
   * each earlier one was a reasonable answer to the previous bug.
   *
   *  r0  a FLAG set on `tutorial:started`, cleared on `tutorial:finished`. The
   *      first run waits for ever by design (LAW #2), so a kid who wandered off
   *      mid-beat never emitted the second half and the gate was gone for the
   *      session: two brothers on one iPad had to close the app to reach each
   *      other's towns, which is the exact flow this feature exists for.
   *  r1  a DEADLINE renewed by the paw's own heartbeat (`tutorial:beat`,
   *      `tutorial:pulse`). The paw re-pulses about every eight idle seconds FOR
   *      EVER, so the hold renewed itself for ever — for exactly the kid it was
   *      meant to protect.
   *  r2  the same deadline, but a pulse could only renew a LIVE `tutorial:claim`.
   *
   * r2 is what the round-2 critic measured, twice, and got two different answers
   * from — and the reason is worse than either of them. Sampled once a second
   * for 45 s on a stalled first run (`tools/fix-visit-r3-probe.mjs --which
   * gate`), the gate BLINKS:
   *
   *      0-3s hidden · 4-22s ON · 23-34s hidden · 35-45s ON
   *
   * with `claimed` true the whole way through. A 20 s hold, a heartbeat that
   * renews it late, and a claim that (measured) does not lapse in practice make
   * a button that comes and goes on a ten-to-twenty-second cycle. That is the
   * worst of the three: "hidden" is a feature you cannot find, but a control
   * that is there when you look and gone when you reach is a game that lies to
   * you, and a three-year-old blames themselves for it.
   *
   * THE FIX IS TO STOP RENEWING ANYTHING. The paw gets ONE window, armed once
   * when a first run actually starts, never extended by a beat or a pulse and
   * never re-armed by a claim coming back after it lapsed. Inside the window the
   * paw has the screen to itself, which is the whole point of the rule; when it
   * ends the gate arrives and STAYS for the rest of that run, whatever the
   * tutorial does afterwards. `finished`, a claim going false and a slot change
   * all end it early. A replay (the paw button) is a new run and gets its own
   * single window, because the kid asked for it with a deliberate tap.
   *
   * There is no state in which this hides the gate twice, and none in which it
   * hides it for ever.
   */
  const TUTORIAL_HOLD_MS = 30_000
  let holdUntil = 0
  /** This first run has already had its one window. Reset only by a NEW run. */
  let holdSpent = false
  const armHold = (): void => {
    if (holdSpent) return
    holdSpent = true
    holdUntil = Date.now() + TUTORIAL_HOLD_MS
    syncVisible()
  }
  const endHold = (): void => {
    holdUntil = 0
    syncVisible()
  }
  let shown: boolean | null = null
  const syncVisible = (): void => {
    const worldly = WORLD_PHASES.includes(app.phase)
    const panelUp = window.__ui?.panel() != null
    const tutorialUp = Date.now() < holdUntil
    /**
     * A GUEST IS NEVER HIDDEN. While a visit is open this layer is carrying the
     * only way home there is — the 124 px button — and the host's face that says
     * whose town this is. Nothing above may take those off the glass: not a
     * phase this crew did not think of, not a panel, and certainly not a
     * tutorial hold left armed from before the door opened. "Impossible to get
     * stuck in" is a requirement, and one boolean is what makes it structural
     * rather than a property of the order events happen to arrive in.
     */
    const on = save.visiting || (worldly && !panelUp && !tutorialUp)
    if (on === shown) return
    shown = on
    layer.style.display = on ? '' : 'none'
  }
  // The window closes on its own and nothing re-reads it until an event happens,
  // so one slow tick keeps the corner honest. Half a second; it writes to the
  // DOM only when the answer actually changes.
  window.setInterval(syncVisible, 500)
  bus.on('ui:panel', (e) => {
    if (e.panel !== null && panelNode !== null) closePanel()
    syncVisible()
  })
  bus.on('app:phaseChanged', syncVisible)
  bus.on('tutorial:started', () => {
    closePanel()
    armHold()
  })
  // A claim going FALSE ends the window early (the kid stopped following the
  // paw). A claim coming back true does NOT re-arm it — that was the blink.
  bus.on('tutorial:claim', (e) => {
    if (!e.claimed) endHold()
  })
  bus.on('tutorial:finished', () => {
    holdSpent = false
    endHold()
  })
  // The other kid taking the iPad is a new first run, and it gets its own window.
  bus.on('save:slotChanged', () => {
    holdSpent = false
    endHold()
  })

  visit.restoreAlbumIds(visitAlbum().photos)
  paint()
  syncVisible()

  // -- test surface (its own global; window.__harness stays architect-owned) -
  const controls = (): HTMLElement[] => [
    ...layer.querySelectorAll<HTMLElement>('button, .mt-present'),
  ]

  window.__visit = {
    ready: true,
    canVisit: () => visit.canVisit,
    visiting: () => save.visiting,
    otherSlot: () => visit.other,
    page: () => page,
    openDoorstep: () => openPanel('main'),
    openGiftShelf: () => openPanel('shelf'),
    closeDoorstep: () => closePanel(),
    enter: () => visit.enter(),
    leave: () => visit.leave(),
    leaveGift: (itemId) => leaveGift(itemId),
    waiting: () => gifts.waiting().length,
    openPresent: () => openPresent(),
    takePhoto: () => takeSouvenir(),
    album: () =>
      visit.souvenirs().map((s) => ({ id: s.id, slot: s.slot, hasImage: s.url !== null })),
    buttons: () =>
      controls().map((n) => {
        const r = n.getBoundingClientRect()
        return {
          label: n.getAttribute('aria-label') ?? n.className,
          w: Math.round(r.width),
          h: Math.round(r.height),
          shown: r.width > 0 && r.height > 0,
        }
      }),
    rect: (label) => {
      const node = controls().find((n) => n.getAttribute('aria-label') === label)
      if (node === undefined) return null
      const r = node.getBoundingClientRect()
      if (r.width === 0 || r.height === 0) return null
      return { x: r.left, y: r.top, w: r.width, h: r.height }
    },
    hidden: () => {
      const out: string[] = []
      const check: readonly (readonly [string, string])[] = [
        ['.mt-purse', 'purse'],
        ['.mt-leftstack', 'rail'],
        ['.mt-botright', 'hammer'],
        ['.mt-botleft', 'undo'],
        ['#mt-build', 'drawer'],
        /**
         * THE WORLD SIGNPOSTS, and this one is not about writing a town.
         *
         * A guest was carrying four of the HUD crew's floating destination
         * chips into their brother's town, and one of them is labelled **Go
         * home** — because in the town you are standing in, the house with the
         * kid's own monster colour on it is "home". It is 88 px, it is at the
         * TOP of the screen where a five-year-old looks first, and it does not
         * go home: it walks you into the HOST's front door. The way out is this
         * crew's 124 px button at the bottom. Two controls reading as home, the
         * smaller and higher one being the wrong one, is the exact shape of bug
         * that made an earlier tool match `/go home/i` and measure the signpost
         * instead of the way out (README, visit-check).
         *
         * The other three are worse than clutter: "Go to bag" opens the SHOP,
         * which is a spending screen, in a town whose save is deliberately
         * refusing every write. A guest tours; a guest does not shop.
         */
        ['.mt-signs', 'signs'],
      ]
      for (const [sel, name] of check) {
        const node = hud.querySelector<HTMLElement>(sel)
        if (node === null) {
          out.push(name)
          continue
        }
        const r = node.getBoundingClientRect()
        if (r.width === 0 && r.height === 0) out.push(name)
      }
      return out
    },
  }
}
